import {
  Content,
  LoadingButton,
  Page,
  TitleNav,
  useBreakPoints,
  useTranslation
} from '@hconnect/uikit'
import {ArrowBackIos, ArrowForward, Add as AddIcon} from '@mui/icons-material'
import {Box, Grid, List, Paper, Typography, Button} from '@mui/material'
import {useFormContext, useFieldArray} from 'react-hook-form'
import {useHistory} from 'react-router'

import {CustomerTier, CustomerTierSettings} from '../types/CustomerTierSettings'

import {CustomerTierListItem} from './CustomerTierListItem'
import {CustomerTierSettingsConstraints} from './CustomerTierSettingsConstraints'
import {useStyles} from './CustomerTierSettingsDetails.styles'

type Props = {
  mode: 'edit' | 'create' | 'copy'
  configId?: string
  customerTierSetting: CustomerTierSettings
  onSaveButtonClick: () => void
  isSubmitting: boolean
}

export const CustomerTierSettingsDetails = ({
  mode,
  customerTierSetting,
  onSaveButtonClick,
  isSubmitting
}: Props) => {
  const {t} = useTranslation()
  const screenSizes = useBreakPoints()
  const isMobile = ['xs', 'sm'].includes(screenSizes)
  const isTablet = ['xs', 'sm', 'md'].includes(screenSizes)
  const {classes} = useStyles({isMobile})
  const history = useHistory()
  const {watch, control} = useFormContext<CustomerTierSettings>()
  const config = watch()
  const isFormReady = Object.keys(config).length > 0

  const {fields, append, remove} = useFieldArray({
    control,
    name: 'tiers'
  })

  const formatTitle = () => {
    switch (mode) {
      case 'edit':
        return t('configurations.hconnect.customerTierSettings.configurationDetails.titleEdit')
      case 'create':
        return t('configurations.hconnect.customerTierSettings.configurationDetails.titleCreate')
      case 'copy':
        return t('configurations.hconnect.customerTierSettings.configurationDetails.titleCopy')
    }
  }

  const handleAddTier = () => {
    const existingNames = new Set(fields.map((field) => (field as CustomerTier).name))
    let newTierName = 'A'
    for (let charCode = 65; charCode <= 90; charCode++) {
      const potentialName = String.fromCharCode(charCode)
      if (!existingNames.has(potentialName)) {
        newTierName = potentialName
        break
      }
    }
    append({name: newTierName, percentage: 1} as CustomerTier)
  }

  const sortedTiersForDisplay = [...fields].sort((a, b) => {
    const nameA = (a as CustomerTier).name || ''
    const nameB = (b as CustomerTier).name || ''
    return nameA.localeCompare(nameB)
  })

  if (!customerTierSetting || !isFormReady) {
    return (
      <div>{t('configurations.hconnect.customerTierSettings.configurationDetails.loading')}</div>
    )
  }

  return (
    <Content>
      <Page
        px={isMobile ? 2 : 6}
        boxed={false}
        title={!isTablet && <Box className={classes.pageTitle}>{formatTitle()}</Box>}
        aboveTitle={
          <Box className={classes.backButton}>
            <TitleNav
              title={
                isTablet
                  ? formatTitle()
                  : t('configurations.hconnect.customerTierSettings.configurationDetails.goBack')
              }
              customTitle={
                !isTablet && (
                  <Box className={classes.pageNav}>
                    <ArrowBackIos />
                    <Typography data-test-id={`customer-tier-settings-title-${mode}`}>
                      {t(
                        'configurations.hconnect.customerTierSettings.configurationDetails.goBack'
                      )}
                    </Typography>
                  </Box>
                )
              }
              onClick={() => history.goBack()}
              className={classes.pageAboveTitle}
            />
          </Box>
        }
      >
        <Grid container className={classes.gridContainer} spacing={2}>
          <Grid item lg={3} md={3} sm={12} xs={12} className={classes.gridItem}>
            <Paper elevation={4} className={classes.paper}>
              <Typography className={classes.cardTitle}>
                {t(
                  'configurations.hconnect.customerTierSettings.configurationDetails.titleConstraints'
                )}
              </Typography>
              <CustomerTierSettingsConstraints disabled={mode === 'edit'} />
            </Paper>
          </Grid>
          <Grid item lg={9} md={9} sm={12} xs={12} className={classes.gridItem}>
            <Paper elevation={4} className={classes.paper}>
              <List>
                {sortedTiersForDisplay.map((sortedField) => {
                  const originalIndex = fields.findIndex((f) => f.id === sortedField.id)
                  if (originalIndex === -1) return null

                  return (
                    <CustomerTierListItem
                      key={sortedField.id}
                      index={originalIndex}
                      onDelete={() => remove(originalIndex)}
                      totalTiers={fields.length}
                    />
                  )
                })}
              </List>
              <Box sx={{display: 'flex', justifyContent: 'flex-start'}}>
                <Button
                  startIcon={<AddIcon fontSize="small" />}
                  variant="contained"
                  color="primary"
                  style={{background: 'rgb(1, 106, 212', textTransform: 'none'}}
                  onClick={handleAddTier}
                  data-test-id="add-customer-tier-button"
                >
                  {t(
                    'configurations.hconnect.customerTierSettings.configurationDetails.buttonAddTier'
                  )}
                </Button>
              </Box>
              <Box className={classes.saveButtonBox}>
                <LoadingButton
                  startIcon={<ArrowForward fontSize="small" />}
                  variant="outlined"
                  color="primary"
                  style={{background: 'rgb(1, 106, 212', textTransform: 'none'}}
                  onClick={onSaveButtonClick}
                  loading={isSubmitting}
                  data-test-id="customer-tier-settings-save-button"
                >
                  {mode === 'edit'
                    ? t(
                        'configurations.hconnect.customerTierSettings.configurationDetails.buttonSave'
                      )
                    : t(
                        `configurations.hconnect.customerTierSettings.configurationDetails.button${
                          mode.charAt(0).toUpperCase() + mode.slice(1)
                        }`
                      )}
                </LoadingButton>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Page>
    </Content>
  )
}
